import {Box, Tab, Tabs, Typography} from '@mui/material';
import React, {useEffect} from 'react';
import {
  TabData,
  TabPanelProps,
} from '../../../user-management/edit-user/EditUser';
import {t} from 'i18next';
import ThreadMap from './thread-map/ThreadMap';
import './DocAnalytics.scss';
import {useDispatch, useSelector} from 'react-redux';
import {
  setDocAnalyticsCurrentTab,
  setDocAnalyticsCheckedDocsCurrentTab,
} from '../../../../redux/slice/docViewSlice';
import DocAnalyticsDataGrid from './doc-analytics-data-grid/DocAnalyticsDataGrid';
import {usePopoutDimensions} from '../../../PortalPopout';
import {useSignalStore} from '../../../PortalPopout/hooks/useSignalStore';
import {RootState} from '../../../../redux/store';

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className={`tabpanel-${value === index ? 'active' : 'hidden'}`}
      {...other}
      data-testid={`doc-analytics-div-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`doc-analytics-div-tab-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}
const localeDocAnalytics = 'docView.docAnalytics';

const DocAnalytics = ({width}: {width: number; panelId?: string}) => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useSignalStore('docAnalytics-tabValue', 0);
  const currentDocAnalyticsTab = useSelector(
    (state: RootState) => state.docView.docAnalytics.currentTab
  );
  const [_popoutSize, setPopoutSize] = useSignalStore<{
    width: number;
    height: number;
  }>('docAnalytics-popoutSize', {width: 0, height: 0});

  const popoutState = usePopoutDimensions({
    throttle: 100,
    onResize: ({width, height}) => {
      setPopoutSize({
        width,
        height: height - 2.8 * 16,
      });
    },
  });

  const effectiveWidth = popoutState.isInPopout ? popoutState.width : width;

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newTabValue: number
  ) => {
    setTabValue(newTabValue);

    dispatch(setDocAnalyticsCheckedDocsCurrentTab(tabData[newTabValue].id));
  };
  const {
    isShowEmailThreadMapTabAnalyticsPanel,
    isShowNearDuplicateTabAnalyticsPanel,
    isShowConceputallySimilarTabAnalyticsPanel,
    isShowFamilyMembersTabAnalyticsPanel,
  } = useSelector((state: RootState) => state.docView.docViewConfiguration);

  const tabData: TabData[] = [
    {
      id: 'thread-map-tab',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="thread-map-tab-label"
        >
          {t(`${localeDocAnalytics}.threadMap`)}
        </Typography>
      ),
      content: (
        <ThreadMap
          width={effectiveWidth}
          noDataLabel={t(`${localeDocAnalytics}.noData.messageForNoThreadMap`)}
        />
      ),
      enabled: isShowEmailThreadMapTabAnalyticsPanel,
    },
    {
      id: 'family-members-tab',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="family-members-tab-label"
        >
          {t(`${localeDocAnalytics}.familyMembers`)}
        </Typography>
      ),
      content: (
        <DocAnalyticsDataGrid
          width={effectiveWidth}
          requestType="FAMILYMEMBER"
          noDataLabel={t(`${localeDocAnalytics}.noData.familyMembers`)}
        />
      ),
      enabled: isShowFamilyMembersTabAnalyticsPanel,
    },
    {
      id: 'near-dupe-tab',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="near-dupe-tab-label"
        >
          {t(`${localeDocAnalytics}.nearDupe`)}
        </Typography>
      ),
      content: (
        <DocAnalyticsDataGrid
          width={effectiveWidth}
          requestType="NEARDUPLICATE"
          noDataLabel={t(`${localeDocAnalytics}.noData.nearDupe`)}
        />
      ),
      enabled: isShowNearDuplicateTabAnalyticsPanel,
    },
    {
      id: 'conceptually-similar-tab',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="conceptually-similar-tab-label"
        >
          {t(`${localeDocAnalytics}.conceptuallySimilar`)}
        </Typography>
      ),
      content: (
        <DocAnalyticsDataGrid
          width={effectiveWidth}
          requestType="CONCEPTUALLYSIMILAR"
          noDataLabel={t(`${localeDocAnalytics}.noData.conceptuallySimilar`)}
        />
      ),
      enabled: isShowConceputallySimilarTabAnalyticsPanel,
    },
  ].filter((tab) => tab.enabled);

  useEffect(() => {
    if (!currentDocAnalyticsTab) return;
    const expectedIndex = tabData.findIndex(
      (tab) => tab.id === currentDocAnalyticsTab
    );
    if (expectedIndex !== -1 && expectedIndex !== tabValue) {
      setTabValue(expectedIndex);
    }
    // Only run this resync check once, on mount.
  }, []);

  useEffect(() => {
    const currentTabId = tabData[tabValue]?.id || '';
    dispatch(setDocAnalyticsCurrentTab(currentTabId));
  }, [
    tabValue,
    dispatch,
    isShowEmailThreadMapTabAnalyticsPanel,
    isShowNearDuplicateTabAnalyticsPanel,
    isShowConceputallySimilarTabAnalyticsPanel,
    isShowFamilyMembersTabAnalyticsPanel,
  ]);

  return (
    <Box className="doc-analytics-container">
      <Tabs
        value={tabValue || 0}
        onChange={handleChangeTab}
        aria-label="tabs"
        className="tabs"
      >
        {tabData.map((tab) => (
          <Tab
            id={tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            key={tab.id}
            label={tab.label}
            className="tab"
            data-testid={tab.id}
          />
        ))}
      </Tabs>
      {tabData.map((tab, index) => (
        <TabPanel key={tab.id} value={tabValue || 0} index={index}>
          {tab.content}
        </TabPanel>
      ))}
    </Box>
  );
};

export default React.memo(DocAnalytics);
